/**
 * Reusable video slide with overlay controls (play/pause, mute/unmute).
 *
 * Designed for use inside a Swiper carousel but works in any context where
 * visibility-based autoplay is desired. Uses the shared Video component's
 * autopilot mode (IntersectionObserver) for play/pause and exposes the
 * player's imperative API for manual controls.
 *
 * Supported video sources: YouTube, Vimeo, direct URL (.mp4, .webm, etc.)
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {
  Box, IconButton,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Video} from '@amblique/king-living/app/components/shared/video';
import {normalizeVideoId} from '@amblique/king-living/app/utils/video-utils';

const DEFAULT_VIDEO_OPTIONS = {
  autopilot: true,
  readonly: true,
  controls: false,

  cc_load_policy: 1,
  cc_lang_pref: 'en',

  texttrack: 'en',

  loop: true,
  mute: true,
  muted: true,
  size: 'contain',
};

const PlayIcon = (props) => (
  <svg fill="currentColor" height="24" viewBox="0 0 24 24" width="24" {...props}>
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = (props) => (
  <svg fill="none" height="20" viewBox="0 0 20 20" width="20" {...props}>
    <path
      d="M6.22 17C6.42408 17 6.59204 16.9313 6.72388 16.794C6.85533 16.6567 6.92105 16.4866 6.92105 16.2836V3.71642C6.92105 3.51343 6.85197 3.34328 6.71381 3.20597C6.57605 3.06866 6.40513 3 6.20105 3C5.99697 3 5.82901 3.06866 5.69717 3.20597C5.56572 3.34328 5.5 3.51343 5.5 3.71642V16.2836C5.5 16.4866 5.56908 16.6567 5.70724 16.794C5.845 16.9313 6.01592 17 6.22 17ZM13.7989 17C14.003 17 14.171 16.9313 14.3028 16.794C14.4343 16.6567 14.5 16.4866 14.5 16.2836V3.71642C14.5 3.51343 14.4309 3.34328 14.2928 3.20597C14.155 3.06866 13.9841 3 13.78 3C13.5759 3 13.408 3.06866 13.2761 3.20597C13.1447 3.34328 13.0789 3.51343 13.0789 3.71642V16.2836C13.0789 16.4866 13.148 16.6567 13.2862 16.794C13.4239 16.9313 13.5949 17 13.7989 17Z"
      fill="currentColor"
    />
  </svg>
);

const VolumeOffIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M10.6504 4.74984V3.8889C8.869 4.11185 7.34776 5.29072 6.69258 6.96859C6.60264 7.19856 6.38092 7.34973 6.13398 7.34984H3.35039V10.1498H5.25039C5.5817 10.1498 5.8503 10.4186 5.85039 10.7498C5.85039 11.0812 5.58176 11.3498 5.25039 11.3498H3.25039C2.64288 11.3498 2.15039 10.8574 2.15039 10.2498V7.24984C2.15049 6.64241 2.64294 6.14984 3.25039 6.14984H5.73945C6.64685 4.22362 8.50125 2.89868 10.6395 2.68031H10.6402C10.9495 2.64923 11.2574 2.75076 11.4879 2.95922C11.7184 3.16768 11.8503 3.46403 11.8504 3.77484V4.74984C11.8504 5.08121 11.5818 5.34984 11.2504 5.34984C10.919 5.34984 10.6504 5.08121 10.6504 4.74984Z" fill="currentColor"/>
    <path d="M10.6498 7.8959C10.6498 7.56466 10.9186 7.29611 11.2498 7.2959C11.5812 7.2959 11.8498 7.56453 11.8498 7.8959V13.7264C11.8493 14.0366 11.7182 14.3322 11.4881 14.5404C11.2579 14.7486 10.9501 14.8499 10.6412 14.8193H10.6405C8.94771 14.6493 7.40333 13.7783 6.38187 12.4178C6.18307 12.1528 6.23646 11.7768 6.5014 11.5779C6.76633 11.3791 7.14228 11.4327 7.34124 11.6975C8.139 12.76 9.33432 13.4491 10.6498 13.6115V7.8959Z" fill="currentColor"/>
    <path d="M13.8261 1.32613C14.0604 1.09181 14.4402 1.09181 14.6746 1.32613C14.9089 1.56044 14.9089 1.94025 14.6746 2.17456L2.17456 14.6746C1.94025 14.9089 1.56044 14.9089 1.32613 14.6746C1.09181 14.4402 1.09181 14.0604 1.32613 13.8261L13.8261 1.32613Z" fill="white"/>
  </svg>
);

const VolumeUpIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="16" height="16" viewBox="0 0 32 32" >
      <path d="M17.953 2.059c-0.326-0.173-0.721-0.154-1.028 0.053l-10.034 7.878-3.332 0.002c-1.704 0-3.090 1.208-3.090 2.7l0.053 6.598c0 1.486 1.386 2.695 3.089 2.695l3.331 0.002 9.981 7.898c0.168 0.113 0.363 0.171 0.559 0.171 0.161 0 0.323-0.039 0.469-0.117 0.327-0.173 0.532-0.512 0.532-0.883v-26.115c0-0.369-0.204-0.709-0.531-0.883zM16.484 27.176l-8.675-7.016c-0.165-0.111-0.36-0.171-0.559-0.171l-3.637-0.002c-0.642 0-1.090-0.366-1.090-0.702l-0.054-6.598c0-0.328 0.448-0.693 1.090-0.693l3.638-0.002c0.199 0 0.393-0.059 0.558-0.171l8.729-7v22.355zM26.515 16.172c0-2.656-1.826-4.889-4.341-5.309-0.553-0.091-1.061 0.277-1.151 0.822s0.277 1.060 0.822 1.151c1.573 0.262 2.671 1.635 2.671 3.336 0 1.62-1.137 3.021-2.703 3.334-0.542 0.108-0.893 0.634-0.785 1.176 0.095 0.476 0.512 0.805 0.98 0.805 0.064 0 0.131-0.006 0.197-0.020 2.498-0.498 4.312-2.726 4.312-5.296zM25.188 6.997c-0.517-0.192-1.094 0.070-1.286 0.588-0.194 0.517 0.070 1.093 0.587 1.286 3.017 1.126 5.043 4.059 5.043 7.301 0 3.243-2.026 6.183-5.041 7.313-0.517 0.195-0.779 0.77-0.586 1.287 0.151 0.402 0.532 0.649 0.937 0.649 0.117 0 0.235-0.020 0.351-0.064 3.792-1.422 6.34-5.113 6.34-9.186s-2.55-7.759-6.344-9.175z"></path>
  </svg>
);

const GalleryVideoSlide = ({
  videoUrl,
  alt,
  isActive,
  isMuted = true,
  showControls = true,
  videoOptions,
  containerProps,
  muteControlProps,
  playPauseControlProps,
  muteButtonProps,
  playPauseButtonProps,
  onMutedChange,
}) => {
  const videoRef = useRef(null);
  const isActiveRef = useRef(isActive);
  /** True when the user paused; kept across swipe/loop until they play or `videoId` changes. */
  const userPausedRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);

  isActiveRef.current = isActive;

  const videoId = useMemo(() => normalizeVideoId(videoUrl), [videoUrl]);

  useEffect(() => {
    userPausedRef.current = false;
  }, [videoId]);
  const mergedOptions = useMemo(
    () => (videoOptions ? {...DEFAULT_VIDEO_OPTIONS, ...videoOptions} : DEFAULT_VIDEO_OPTIONS),
    [videoOptions]
  );

  /**
   * Off-screen (inactive) slides should not steal keyboard focus. Only the active slide
   * should leave iframes / HTML5 video in the natural tab order so users can focus embed
   * controls; inactive slides get tabindex -1 on embeds. Swiper a11y + MutationObserver
   * may fight third-party rewrites of tabindex — observer re-applies using isActiveRef.
   */
  useEffect(() => {
    const applyEmbedTabindex = (root) => {
      if (!root) return;
      const active = isActiveRef.current;
      root.querySelectorAll('iframe, video').forEach((node) => {
        if (active) {
          node.removeAttribute('tabindex');
        } else {
          node.setAttribute('tabindex', '-1');
        }
      });
    };

    let mo = null;
    const attach = () => {
      const root = videoRef.current?.element;
      if (!root) return false;
      applyEmbedTabindex(root);
      mo?.disconnect();
      mo = new MutationObserver(() => applyEmbedTabindex(root));
      mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['tabindex'] });
      return true;
    };

    if (attach()) {
      return () => {
        mo?.disconnect();
      };
    }

    let attempts = 0;
    const maxAttempts = 150;
    const interval = setInterval(() => {
      attempts += 1;
      if (attach()) {
        clearInterval(interval);
        return;
      }
      if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
      mo?.disconnect();
    };
  }, [videoId, isActive]);

  useEffect(() => {
    if (!isActive) {
      setIsPlaying(false);
    } else {
      setIsPlaying(!userPausedRef.current);
    }
  }, [isActive]);

  // Drive player from `isActive` + `userPausedRef` (slide inactive → pause; return → play unless user had paused).
  useEffect(() => {
    const apply = () => {
      const player = videoRef.current?.player;
      if (!player) {
        return false;
      }
      if (isActive) {
        if (userPausedRef.current) {
          const p = player.pause?.();
          if (p && typeof p.catch === 'function') {
            p.catch(() => {});
          }
          setIsPlaying(false);
        } else {
          const r = player.play?.();
          if (r && typeof r.catch === 'function') {
            r.catch(() => {});
          }
          setIsPlaying(true);
        }
      } else {
        const r = player.pause?.();
        if (r && typeof r.catch === 'function') {
          r.catch(() => {});
        }
        setIsPlaying(false);
      }
      return true;
    };

    if (apply()) {
      return undefined;
    }

    let n = 0;
    const id = setInterval(() => {
      n += 1;
      if (apply() || n >= 40) {
        clearInterval(id);
      }
    }, 125);

    return () => clearInterval(id);
  }, [isActive, videoId]);

  // Re-apply parent `isMuted` / `videoMutedMap` to the player when the slide is active.
  useEffect(() => {
    if (!isActive) return;

    const player = videoRef.current?.player;

    if (!player) return;

    if (isMuted) {
      player.mute();
    } else {
      player.unMute();
    }
  }, [isActive, isMuted]);

  const togglePlay = useCallback(() => {
    const player = videoRef.current?.player;

    if (!player) return;

    if (isPlaying) {
      player.pause();
      userPausedRef.current = true;
      setIsPlaying(false);
    } else {
      player.play();
      userPausedRef.current = false;
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    const player = videoRef.current?.player;

    if (!player) return;

    if (isMuted) {
      player.unMute();
      onMutedChange?.(false);
    } else {
      player.mute();
      onMutedChange?.(true);
    }
  }, [isMuted, onMutedChange]);

  if (!videoId) return null;

  return (
    <Box {...containerProps}>
      {showControls && (
        <>
          <Box {...muteControlProps}>
            <IconButton
              aria-label={isMuted ? 'Unmute video' : 'Mute video'}
              icon={isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              variant="unstyled"
              onClick={toggleMute}
              {...muteButtonProps}
              tabIndex={isActive ? 0 : -1}
            />
          </Box>

          <Box {...playPauseControlProps}>
            <IconButton
              aria-label={isPlaying ? 'Pause video' : 'Play video'}
              icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
              variant="unstyled"
              onClick={togglePlay}
              {...playPauseButtonProps}
              tabIndex={isActive ? 0 : -1}
            />
          </Box>
        </>
      )}

      <Video
        ref={videoRef}
        aria-label={alt}
        id={videoId}
        options={mergedOptions}
        width="100%"
      />
    </Box>
  );
};

GalleryVideoSlide.propTypes = {
  /** Accessible label for the video */
  alt: PropTypes.string,
  /** Chakra style props for the outer Box container (video slide) */
  containerProps: PropTypes.object,
  /** Wrapper Box for mute — absolute bottom-left */
  muteControlProps: PropTypes.object,
  /** IconButton styles for mute */
  muteButtonProps: PropTypes.object,
  /** Wrapper Box for play/pause — absolute bottom-right */
  playPauseControlProps: PropTypes.object,
  /** IconButton styles for play/pause */
  playPauseButtonProps: PropTypes.object,
  /** Whether this slide is currently the active/visible slide */
  isActive: PropTypes.bool,
  /** Controlled muted state (lifted to parent for persistence across slide transitions) */
  isMuted: PropTypes.bool,
  /** When false, hide mute/play overlay controls (e.g. PLP product tiles). */
  showControls: PropTypes.bool,
  /** Video component options (merged with defaults; see DEFAULT_VIDEO_OPTIONS) */
  videoOptions: PropTypes.object,
  /** Full video URL — YouTube, Vimeo, or direct (.mp4, .webm, etc.) */
  videoUrl: PropTypes.string.isRequired,
  /** Called with the new muted boolean when the user toggles mute */
  onMutedChange: PropTypes.func,
};

export default GalleryVideoSlide;
